import React from "react"

// nextjs
import Link from "next/link"

// styling
import styled from "@emotion/styled"

// firebase
import { Auth } from "services/firebase"
import { signInAnonymously } from "firebase/auth"

// components
import {
  Row,
  Col,
  Space,
  Typography,
  Button,
  message,
  Divider,
  Checkbox,
  Form,
  Flex,
} from "antd"
import {
  LoginOutlined,
  AliwangwangFilled,
  LoadingOutlined,
} from "@ant-design/icons"
import Logo from "components/Logo"

// context
import { useAuth } from "store/auth"

// modals
import LoginModal from "modals/Login"
import SignupModal from "modals/Signup"

// store
import { useFunnel } from "store/funnel"
import { useTranslation } from "react-i18next"

const Selection = styled.div`
  width: 100%;
  margin-top: 40px;
`

const SelectionPanel = styled.div`
  width: 230px;
  height: 30vh;

  padding: 32px 16px;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;

  text-align: center;

  background-color: rgba(185, 185, 185, 0.1);

  @media (max-width: 820px) {
    width: 300px;
    height: 200px;
    justify-content: center;
    margin: 2px;
  }

  &:hover {
    background-color: rgba(185, 185, 185, 0.15);
  }
`

const AuthBox = () => {
  const { isAuthentificated, status } = useAuth()
  const { t } = useTranslation()

  const funnel = useFunnel()

  const [messageApi, contextHolder] = message.useMessage()
  const [showLogin, setShowLogin] = React.useState(false)
  const [showSignup, setShowSignup] = React.useState(false)
  const [acceptedTerms, setAcceptedTerms] = React.useState(false)
  const [state, setState] = React.useState("idle")

  if (status !== "ready")
    return (
      <Divider>
        <LoadingOutlined spin style={{ fontSize: 24 }} />
      </Divider>
    )

  if (state === "awaiting-authentication-response")
    return (
      <Divider>
        <Space>
          <LoadingOutlined spin style={{ fontSize: 24 }} />

          <Typography.Text>{t("authenticating")}</Typography.Text>
        </Space>
      </Divider>
    )

  if (isAuthentificated) return null

  return (
    <div>
      {contextHolder}
      {status === "ready" && (
        <LoginModal
          open={showLogin}
          onCancel={() => setShowLogin(false)}
          onFinish={() => setState("awaiting-authentication-response")}
        />
      )}
      {status === "ready" && (
        <SignupModal
          open={showSignup}
          onCancel={() => setShowSignup(false)}
          onFinish={() => setState("awaiting-authentication-response")}
        />
      )}

      {!isAuthentificated && (
        <Selection>
          <Row gutter={4} justify="center" style={{ width: "100%" }}>
            <Col>
              <SelectionPanel>
                <Typography.Title level={5}>
                  {t("without_account")}
                </Typography.Title>

                <Typography.Paragraph>
                  {t("without_account_subtitle")}
                </Typography.Paragraph>

                <Form
                  name="anonymous-login"
                  onFinish={async (values) => {
                    if (!values.terms) {
                      return
                    }

                    try {
                      setState("log-in-anonymously")

                      await signInAnonymously(Auth)

                      funnel.add("login_anonymously")
                    } catch (e) {
                      setState("error")
                      messageApi.error(e.message)
                    }
                  }}
                >
                  <Form.Item>
                    <Button
                      htmlType="submit"
                      data-testid="login-anonymously-button"
                      disabled={status !== "ready"}
                      loading={state === "log-in-anonymously"}
                      icon={<AliwangwangFilled />}
                      type={acceptedTerms ? "primary" : "default"}
                      onClick={() => {
                        if (!acceptedTerms) {
                          messageApi.error(t("login_asterisk"))
                        }
                      }}
                    >
                      {t("continue_as_guest")}
                    </Button>
                  </Form.Item>

                  <Form.Item
                    style={{ padding: "0 40px" }}
                    valuePropName="checked"
                    name="terms"
                    rules={[
                      {
                        required: true,
                        message: t("please_accept_terms"),
                      },
                    ]}
                  >
                    <Checkbox
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    >
                      {t("i_accept_the")}
                      <Link target="_blank" href="/terms">
                        {t("privacy_and_terms")}
                      </Link>
                    </Checkbox>
                  </Form.Item>
                </Form>
              </SelectionPanel>
            </Col>
            <Col>
              <SelectionPanel>
                <Typography.Title level={5}>
                  {t("with_account")}
                </Typography.Title>

                <Typography.Paragraph>
                  {t("with_account_subtitle")}
                </Typography.Paragraph>

                <Space size={10} direction="vertical" align="center">
                  <Button
                    data-testid="signup-button"
                    disabled={status !== "ready"}
                    type="dashed"
                    onClick={() => setShowSignup(true)}
                  >
                    {t("sign_up_new_account")}
                  </Button>
                  <Button
                    data-testid="login-button"
                    disabled={status !== "ready"}
                    icon={<LoginOutlined />}
                    type="primary"
                    onClick={() => setShowLogin(true)}
                  >
                    {t("login_btn")}
                  </Button>
                </Space>
              </SelectionPanel>
            </Col>
          </Row>

          <Flex
            justify="center"
            align="center"
            style={{ marginTop: 12 }}
            gap={8}
          >
            <Typography.Text type="secondary" style={{ fontStyle: "italic" }}>
              provided by
            </Typography.Text>
            <Logo />
          </Flex>
        </Selection>
      )}
    </div>
  )
}

export default AuthBox
